import { MOSIDA_API_BASE_URL } from "../config/apiUrls";

export interface Product {
    id: number;
    name: string | { name?: string;[key: string]: any };
    type: string;
    picture?: string;
    finalCanvasImage?: any;
    sku_code?: string;
    price?: number | string;
    description?: string | { description?: string;[key: string]: any };
    artwork_placement?: unknown;
    artworkPlacement?: unknown;
    print_area?: unknown;
    product_sizes?: Array<{
        id: number | string;
        size: string | { name?: string; size?: string;[key: string]: any };
        price: number;
        inches?: string | number | null;
        color?: string | { name?: string;[key: string]: any } | null;
        quantity?: number | null;
    }>;
    color?: Array<string | { name?: string; productImageUrl?: string }>;
    brand_name?: string;
    quantity?: number | null;
    [key: string]: any;
}

/** Default CDN host used by Mosida product media when API returns relative paths. */
const DEFAULT_PRODUCT_MEDIA_CDN = "https://d2s4ngnid78ki4.cloudfront.net";

function isUsableProductImage(value: unknown): value is string {
    if (typeof value !== "string") return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    const lower = trimmed.toLowerCase();
    return lower !== "null" && lower !== "undefined" && lower !== "[object object]";
}

function encodeUrlSpaces(url: string): string {
    return url.replace(/ /g, "%20");
}

function extractMediaCdnBase(hint?: string | null): string | undefined {
    if (!isUsableProductImage(hint) || !/^https?:\/\//i.test(hint.trim())) return undefined;
    try {
        const parsed = new URL(hint.trim());
        return `${parsed.protocol}//${parsed.host}`;
    } catch {
        return undefined;
    }
}

/**
 * Pack/product APIs sometimes return relative media paths
 * (e.g. `main/collage/...png` or `4954060-file.png`). Turn those into
 * absolute CDN URLs and encode spaces so <img> can load them.
 */
export function normalizeMediaUrl(
    value?: string | null,
    baseHint?: string | null
): string | undefined {
    if (!isUsableProductImage(value)) return undefined;
    const trimmed = value.trim();

    if (/^(data:|blob:)/i.test(trimmed)) return trimmed;
    if (/^https?:\/\//i.test(trimmed)) return encodeUrlSpaces(trimmed);
    if (trimmed.startsWith("//")) return encodeUrlSpaces(`https:${trimmed}`);

    const base =
        extractMediaCdnBase(baseHint) ||
        DEFAULT_PRODUCT_MEDIA_CDN;
    const path = trimmed.replace(/^\/+/, "");
    return encodeUrlSpaces(`${base.replace(/\/+$/, "")}/${path}`);
}

function firstNormalizedImage(
    candidates: unknown[],
    baseHint?: string | null
): string | undefined {
    for (const candidate of candidates) {
        const normalized = normalizeMediaUrl(
            typeof candidate === "string" ? candidate : undefined,
            baseHint
        );
        if (normalized) return normalized;
    }
    return undefined;
}

function productMediaHint(product: any): string | undefined {
    return [
        product?.picture,
        product?.image,
        product?.imgURL,
        product?.image_url,
    ].find((value) => isUsableProductImage(value) && /^https?:\/\//i.test(value.trim()));
}

/**
 * Prefers the product's display image and only falls back to colour variant
 * images (starting at index 0) when no display image was uploaded.
 */
export function resolveProductDisplayImage(product: any): string | undefined {
    const hint = productMediaHint(product);
    const colorImages = Array.isArray(product?.color)
        ? product.color.flatMap((color: any) => {
            if (!color || typeof color !== "object") return [];
            return [
                color.productImageUrl,
                color.product_image_url,
                color.picture,
                color.image,
                color.imgURL,
                color.imageUrl,
                color.image_url,
            ];
        })
        : [];

    return firstNormalizedImage(
        [
            product?.picture,
            product?.image,
            product?.imgURL,
            product?.image_url,
            ...colorImages,
        ],
        hint
    );
}

/** Resolve a color variant image, falling back to the product display image. */
export function resolveProductColorImage(product: any, colorLabel?: string | null): string | undefined {
    const colors = Array.isArray(product?.color) ? product.color : [];
    const normalized = String(colorLabel || "").trim().toLowerCase();
    const hint = productMediaHint(product);

    if (normalized) {
        const matched = colors.find((color: any) => {
            if (typeof color === "string") return color.trim().toLowerCase() === normalized;
            if (!color || typeof color !== "object") return false;
            const label = String(
                color.name ||
                color.label ||
                color.value ||
                (typeof color.color === "string" ? color.color : color.color?.name) ||
                ""
            )
                .trim()
                .toLowerCase();
            return label === normalized;
        });

        if (matched && typeof matched === "object") {
            const colorImage = firstNormalizedImage(
                [
                    matched.productImageUrl,
                    matched.product_image_url,
                    matched.picture,
                    matched.image,
                    matched.imgURL,
                    matched.imageUrl,
                    matched.image_url,
                ],
                hint
            );
            if (colorImage) return colorImage;
        }
    }

    return resolveProductDisplayImage(product);
}

export interface ProductApiResponse {
    success?: boolean;
    data?: Product[];
    products?: Product[];
    message?: string;
    error?: string;
}

/** Share one /product/all network call across StrictMode remounts and concurrent callers. */
let fetchAllProductsInFlight: Promise<ProductApiResponse> | null = null;
let fetchAllProductsCache: ProductApiResponse | null = null;

export async function fetchAllProducts(options?: {
    /** Bypass in-memory cache and hit /product/all again. */
    forceRefresh?: boolean;
}): Promise<ProductApiResponse> {
    const forceRefresh = Boolean(options?.forceRefresh);
    if (
        !forceRefresh &&
        fetchAllProductsCache?.success &&
        Array.isArray(fetchAllProductsCache.data)
    ) {
        return fetchAllProductsCache;
    }
    if (!forceRefresh && fetchAllProductsInFlight) return fetchAllProductsInFlight;

    const request = (async (): Promise<ProductApiResponse> => {
        try {
            const baseUrl = MOSIDA_API_BASE_URL;
            const response = await fetch(`${baseUrl}/product/all`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                return {
                    success: false,
                    error: `Failed to fetch products: ${response.status} ${errorText}`,
                    data: [],
                };
            }

            const data = await response.json();

            // Handle different response formats
            const products = data?.data || data?.products || data || [];

            const result: ProductApiResponse = {
                success: true,
                data: Array.isArray(products) ? products : [],
            };
            fetchAllProductsCache = result;
            return result;
        } catch (error: any) {
            return {
                success: false,
                error: error?.message || 'Network error while fetching products',
                data: [],
            };
        }
    })();

    fetchAllProductsInFlight = request;
    try {
        return await request;
    } finally {
        if (fetchAllProductsInFlight === request) {
            fetchAllProductsInFlight = null;
        }
    }
}

export function cleanOptionLabel(value: unknown): string {
    if (typeof value !== "string") return "";
    const trimmed = value.trim();
    if (!trimmed || trimmed === "[object Object]") return "";
    return trimmed;
}

export function getProductDisplayName(name: unknown): string {
    if (typeof name === "string") return name.trim();
    if (name && typeof name === "object") {
        const nested = (name as { name?: unknown }).name;
        if (typeof nested === "string") return nested.trim();
    }
    return "";
}

export function productNameIncludesSticker(name: unknown): boolean {
    return getProductDisplayName(name).toLowerCase().includes("sticker");
}

export function asOptionArray(value: unknown): any[] {
    if (Array.isArray(value)) return value.filter((item) => item != null);
    if (value && typeof value === "object") return [value];
    if (typeof value === "string" && value.trim()) return [value];
    return [];
}

export function getProductSizeLabel(size: any): string {
    if (!size) return "";
    if (typeof size === "string") return cleanOptionLabel(size);

    const raw = size.size;
    if (typeof raw === "string") return cleanOptionLabel(raw);
    if (raw && typeof raw === "object") {
        return (
            cleanOptionLabel(raw.size) ||
            cleanOptionLabel(raw.name) ||
            cleanOptionLabel(raw.label) ||
            cleanOptionLabel(raw.value)
        );
    }

    return (
        cleanOptionLabel(size.name) ||
        cleanOptionLabel(size.label) ||
        cleanOptionLabel(size.value) ||
        ""
    );
}

/** Size label plus inches when the API provides a separate `inches` field (e.g. stickers). */
export function formatProductSizeWithInches(size: any): string {
    const label = getProductSizeLabel(size);
    const inches = cleanOptionLabel(String(size?.inches ?? ""));
    if (!inches) return label;

    const lower = label.toLowerCase();
    if (lower.includes("inch") || lower.includes(inches.toLowerCase())) {
        return label;
    }
    return `${label} ${inches} inches`.trim();
}

export function getProductSizeInches(size: any): string | undefined {
    const inches = cleanOptionLabel(String(size?.inches ?? ""));
    return inches || undefined;
}

export function getProductColorLabel(color: any): string {
    if (!color) return "";
    if (typeof color === "string") return cleanOptionLabel(color);
    if (typeof color !== "object") return "";

    const nestedColor = color.color;
    const nestedColour = color.colour;

    return (
        cleanOptionLabel(color.name) ||
        cleanOptionLabel(typeof nestedColor === "string" ? nestedColor : nestedColor?.name) ||
        cleanOptionLabel(typeof nestedColour === "string" ? nestedColour : nestedColour?.name) ||
        cleanOptionLabel(color.label) ||
        cleanOptionLabel(color.value) ||
        cleanOptionLabel(color.title) ||
        cleanOptionLabel(color.color_name) ||
        cleanOptionLabel(color.colorName) ||
        ""
    );
}

export function uniqueByLabel<T>(
    items: T[],
    getLabel: (item: T) => string,
    preferItem?: (current: T, next: T) => boolean
): T[] {
    const byLabel = new Map<string, T>();

    items.forEach((item) => {
        const label = getLabel(item).trim().toLowerCase();
        if (!label) return;
        const existing = byLabel.get(label);
        if (!existing) {
            byLabel.set(label, item);
            return;
        }
        if (preferItem?.(existing, item)) {
            byLabel.set(label, item);
        }
    });

    return Array.from(byLabel.values());
}

/** Apparel size rank: XS → S → M → L → XL → 2XL → 3XL… */
const SIZE_TOKEN_PATTERNS: Array<{ re: RegExp; rank: number }> = [
    { re: /\b(5xl|xxxxxl)\b/i, rank: 9 },
    { re: /\b(4xl|xxxxl)\b/i, rank: 8 },
    { re: /\b(3xl|xxxl|extra\s*extra\s*large)\b/i, rank: 7 },
    { re: /\b(2xl|2x|xxl|xxlarge)\b/i, rank: 6 },
    { re: /\b(xl|x-large|xlarge|extra\s*large)\b/i, rank: 5 },
    { re: /\b(large|lg)\b/i, rank: 4 },
    { re: /\b(medium|med|md)\b/i, rank: 3 },
    { re: /\b(small|sm)\b/i, rank: 2 },
    { re: /\b(xs|x-small|xsmall|extra\s*small)\b/i, rank: 1 },
];

function sizeCategoryRank(label: string): number {
    const n = label.toLowerCase();
    if (/\byouth\b|\bkids?\b|\bjunior\b|\btoddler\b|\bbaby\b/.test(n)) return 1;
    if (/\badult\b|\bmen'?s\b|\bwomen'?s\b|\bunisex\b/.test(n)) return 0;
    return 0;
}

function sizeSortRank(label: string): number {
    const normalized = label.trim().toLowerCase().replace(/["']/g, "").replace(/\s+/g, " ");
    if (!normalized) return 1000;

    for (const { re, rank } of SIZE_TOKEN_PATTERNS) {
        if (re.test(normalized)) return rank;
    }

    // Numeric / inches sizes (e.g. stickers "3x3") — sort by first number.
    const numeric = normalized.match(/(\d+(\.\d+)?)/);
    if (numeric) return 200 + Number(numeric[1]);

    return 1000;
}

export function sortSizesStandard<T>(items: T[], getLabel: (item: T) => string): T[] {
    return [...items].sort((a, b) => {
        const labelA = getLabel(a);
        const labelB = getLabel(b);
        // Adult before Youth, then Small → Medium → Large → XL → 2XL…
        const categoryDiff = sizeCategoryRank(labelA) - sizeCategoryRank(labelB);
        if (categoryDiff !== 0) return categoryDiff;
        const rankDiff = sizeSortRank(labelA) - sizeSortRank(labelB);
        if (rankDiff !== 0) return rankDiff;
        return labelA.localeCompare(labelB, undefined, { sensitivity: "base", numeric: true });
    });
}

export function uniqueSortedProductSizes(raw: unknown): any[] {
    return sortSizesStandard(
        uniqueByLabel(asOptionArray(raw), getProductSizeLabel),
        getProductSizeLabel
    );
}

export function uniqueProductColors(
    raw: unknown,
    preferItem?: (current: any, next: any) => boolean
): any[] {
    return uniqueByLabel(asOptionArray(raw), getProductColorLabel, preferItem);
}

/** Light garment colors shown first on merch cards / modals. */
const LIGHT_COLOR_LABELS = [
    "white",
    "ash",
    "beige",
    "natural",
    "cream",
    "ivory",
    "off-white",
    "off white",
    "heather",
    "sport grey",
    "sport gray",
    "light grey",
    "light gray",
    "grey",
    "gray",
] as const;

const DARK_COLOR_LABELS = [
    "black",
    "navy",
    "charcoal",
    "dark",
    "midnight",
] as const;

function normalizeColorLabel(value: unknown): string {
    return getProductColorLabel(value).trim().toLowerCase();
}

function isDarkColorLabel(label: string): boolean {
    return DARK_COLOR_LABELS.some(
        (dark) => label === dark || label.includes(dark)
    );
}

function lightColorRank(label: string): number {
    const exact = LIGHT_COLOR_LABELS.indexOf(
        label as (typeof LIGHT_COLOR_LABELS)[number]
    );
    if (exact >= 0) return exact;
    if (isDarkColorLabel(label)) return LIGHT_COLOR_LABELS.length + 10;
    return LIGHT_COLOR_LABELS.length;
}

/** Product-specific first choice when that light color exists. */
function preferredColorLabelForProduct(productHint?: {
    name?: unknown;
    sku_code?: unknown;
} | null): string | null {
    const name = getProductDisplayName(productHint?.name).toLowerCase();
    const sku = String(productHint?.sku_code || "")
        .trim()
        .toLowerCase();
    const haystack = `${name} ${sku}`.trim();
    if (!haystack) return "white";

    if (haystack.includes("tote") || sku === "tb" || sku.startsWith("tb")) {
        return "beige";
    }
    if (
        haystack.includes("hoodie") ||
        sku === "hd" ||
        sku.startsWith("hd")
    ) {
        // Hoodie catalog uses Ash as the light option.
        return "ash";
    }
    if (
        haystack.includes("t shirt") ||
        haystack.includes("t-shirt") ||
        haystack.includes("tshirt") ||
        haystack.includes("cap") ||
        haystack.includes("hat") ||
        sku === "ts" ||
        sku.startsWith("ts") ||
        sku === "cap" ||
        sku.startsWith("cap")
    ) {
        return "white";
    }
    return "white";
}

/**
 * Prefer a light product color when available
 * (T Shirt/Cap → White, Hoodie → Ash, Tote → Beige). Falls back through
 * other light labels before dark colors.
 */
export function findPreferredProductColor(
    raw: unknown,
    productHint?: { name?: unknown; sku_code?: unknown } | null
): any | null {
    const colors = uniqueProductColors(raw);
    if (!colors.length) return null;

    const preferredLabel = preferredColorLabelForProduct(productHint);
    if (preferredLabel) {
        const preferred = colors.find(
            (color) => normalizeColorLabel(color) === preferredLabel
        );
        if (preferred) return preferred;
    }

    for (const label of LIGHT_COLOR_LABELS) {
        const match = colors.find(
            (color) => normalizeColorLabel(color) === label
        );
        if (match) return match;
    }

    const nonDark = colors.find(
        (color) => !isDarkColorLabel(normalizeColorLabel(color))
    );
    if (nonDark) return nonDark;

    return colors[0];
}

/** Sort colors light-first for pickers (White/Ash before Black). */
export function sortProductColorsLightFirst(raw: unknown): any[] {
    const colors = uniqueProductColors(raw);
    return [...colors].sort((a, b) => {
        const rankDiff =
            lightColorRank(normalizeColorLabel(a)) -
            lightColorRank(normalizeColorLabel(b));
        if (rankDiff !== 0) return rankDiff;
        return normalizeColorLabel(a).localeCompare(normalizeColorLabel(b));
    });
}

/** Parse API stock quantity; null when the field is absent / non-numeric. */
export function parseStockQuantity(value: unknown): number | null {
    if (value == null || value === "") return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return Math.max(0, Math.floor(n));
}

function normalizeStockLabel(value: unknown): string {
    return String(value || "")
        .trim()
        .toLowerCase();
}

function productSizeRowColorLabel(row: any): string {
    if (!row) return "";
    const fromColor = getProductColorLabel(row.color);
    if (fromColor) return fromColor;
    if (typeof row.color === "string") return cleanOptionLabel(row.color);
    return "";
}

/**
 * Raw product_sizes rows matching optional size / color labels.
 * Color match is skipped when the row has no color (e.g. stickers, cap).
 */
export function findProductSizeRows(
    product: any,
    options?: { sizeLabel?: string | null; colorLabel?: string | null }
): any[] {
    const rows = asOptionArray(product?.product_sizes);
    if (!rows.length) return [];

    const sizeLabel = normalizeStockLabel(options?.sizeLabel || "");
    const colorLabel = normalizeStockLabel(options?.colorLabel || "");

    return rows.filter((row) => {
        const rowSize =
            formatProductSizeWithInches(row) || getProductSizeLabel(row);
        if (sizeLabel) {
            const rowSizeNorm = normalizeStockLabel(rowSize);
            const rowSizeOnly = normalizeStockLabel(getProductSizeLabel(row));
            if (rowSizeNorm !== sizeLabel && rowSizeOnly !== sizeLabel) {
                return false;
            }
        }

        const rowColor = normalizeStockLabel(productSizeRowColorLabel(row));
        if (!colorLabel) return true;
        // Size rows without a color apply to any color selection (or no-color products).
        if (!rowColor) return true;
        return rowColor === colorLabel;
    });
}

/**
 * Stock for a specific size + color selection from `product_sizes[].quantity`.
 * Falls back to product-level `quantity` only when there are no size rows.
 */
export function getVariantStockQuantity(
    product: any,
    options?: {
        size?: any;
        color?: any;
        sizeLabel?: string | null;
        colorLabel?: string | null;
    }
): number | null {
    const sizeLabel =
        options?.sizeLabel ||
        (options?.size
            ? formatProductSizeWithInches(options.size) ||
              getProductSizeLabel(options.size)
            : "");
    const colorLabel =
        options?.colorLabel ||
        (options?.color ? getProductColorLabel(options.color) : "");

    const rows = asOptionArray(product?.product_sizes);
    if (rows.length) {
        const matched = findProductSizeRows(product, { sizeLabel, colorLabel });
        if (!matched.length) {
            // Selection has no matching variant row → treat as unavailable.
            if (sizeLabel || colorLabel) return 0;
            const totals = rows
                .map((row) => parseStockQuantity(row?.quantity))
                .filter((q): q is number => q != null);
            if (!totals.length) return null;
            return totals.reduce((sum, q) => sum + q, 0);
        }

        const quantities = matched
            .map((row) => parseStockQuantity(row?.quantity))
            .filter((q): q is number => q != null);
        if (!quantities.length) return null;
        return quantities.reduce((sum, q) => sum + q, 0);
    }

    return parseStockQuantity(product?.quantity);
}

/** Stickers skip inventory checks (API quantity is ignored). */
export function shouldSkipStockCheck(product: any): boolean {
    return productNameIncludesSticker(product?.name);
}

/** Max purchasable units for the given selection (0 when sold out). */
export function getAvailableStock(
    product: any,
    options?: {
        size?: any;
        color?: any;
        sizeLabel?: string | null;
        colorLabel?: string | null;
    }
): number {
    // Stickers: do not enforce quantity / sold-out from the catalog API.
    if (shouldSkipStockCheck(product)) return Number.MAX_SAFE_INTEGER;
    return getVariantStockQuantity(product, options) ?? 0;
}

export function isVariantInStock(
    product: any,
    options?: {
        size?: any;
        color?: any;
        sizeLabel?: string | null;
        colorLabel?: string | null;
    }
): boolean {
    if (shouldSkipStockCheck(product)) return true;
    return getAvailableStock(product, options) > 0;
}

/**
 * Product-level stock for cards.
 * - No color filter → sum across all size/color rows (card "Sold out" only when every variant is 0).
 * - With `colorLabel` → that color only (modal / preferred-variant checks).
 */
export function getProductStockQuantity(
    product: any,
    options?: { colorLabel?: string | null; color?: any }
): number | null {
    if (shouldSkipStockCheck(product)) return Number.MAX_SAFE_INTEGER;

    const colorLabel =
        options?.colorLabel ||
        (options?.color ? getProductColorLabel(options.color) : "");
    const rows = asOptionArray(product?.product_sizes);

    if (rows.length) {
        const matched = colorLabel
            ? findProductSizeRows(product, { colorLabel })
            : rows;
        const quantities = matched
            .map((row) => parseStockQuantity(row?.quantity))
            .filter((q): q is number => q != null);
        if (!quantities.length) return null;
        return quantities.reduce((sum, q) => sum + q, 0);
    }

    return parseStockQuantity(product?.quantity);
}

/** True when any size/color variant has stock (omit color to check the whole product). */
export function isProductInStock(
    product: any,
    options?: { colorLabel?: string | null; color?: any }
): boolean {
    if (shouldSkipStockCheck(product)) return true;
    return (getProductStockQuantity(product, options) ?? 0) > 0;
}

/** First sorted size that has stock for the optional color; else first size. */
export function findPreferredProductSize(
    product: any,
    color?: any
): any | null {
    const sizes = uniqueSortedProductSizes(product?.product_sizes);
    if (!sizes.length) return null;
    const colorLabel = color ? getProductColorLabel(color) : "";
    const inStock = sizes.find((size) =>
        isVariantInStock(product, { size, colorLabel })
    );
    return inStock || sizes[0];
}

export function isSizeAvailableForColor(
    product: any,
    size: any,
    color?: any
): boolean {
    return isVariantInStock(product, {
        size,
        colorLabel: color ? getProductColorLabel(color) : "",
    });
}

export function isColorAvailableForSize(
    product: any,
    color: any,
    size?: any
): boolean {
    return isVariantInStock(product, {
        size,
        colorLabel: getProductColorLabel(color),
    });
}

